import { t } from '../i18n.js';
import { twoFactorVerify, completeLoginAfterTwoFactor } from '../services/auth.js';

/**
 * Creates the 2FA verification screen (returning users with 2FA already enabled).
 *
 * Supports both TOTP codes and recovery codes.
 *
 * @param {string} tempToken  Temporary JWT from login response
 * @param {string} instance   CRM instance name
 * @param {function} onComplete  Called after successful verification (login done)
 * @param {function} onCancel    Called when user cancels back to login
 * @returns {HTMLElement}
 */
export function createTwoFactorVerifyScreen(tempToken, instance, onComplete, onCancel) {
  const container = document.createElement('div');
  container.className = 'tfa-container';

  let useRecovery = false;

  function render() {
    container.innerHTML = `
      <div class="tfa-card">
        <h2 class="tfa-title">${t('twoFactorVerifyTitle')}</h2>
        <div class="tfa-shield-icon">🔐</div>
        ${useRecovery ? renderRecoveryMode() : renderCodeMode()}
        <button type="button" class="tfa-back-link" data-action="back-to-login">${t('twoFactorBackToLogin')}</button>
      </div>
    `;
    attachEvents();
  }

  function renderCodeMode() {
    return `
      <div class="tfa-step-content">
        <p class="tfa-description">${t('twoFactorEnterCode')}</p>
        <div class="tfa-code-input-group">
          <input type="text" class="tfa-code-input" maxlength="6" pattern="[0-9]{6}" placeholder="000000" autocomplete="one-time-code" inputmode="numeric" />
        </div>
        <div class="tfa-error" style="display:none;"></div>
        <button type="button" class="tfa-primary-btn" data-action="verify">${t('twoFactorStepVerify')}</button>
        <button type="button" class="tfa-toggle-link" data-action="toggle-mode">${t('twoFactorUseRecovery')}</button>
      </div>
    `;
  }

  function renderRecoveryMode() {
    return `
      <div class="tfa-step-content">
        <p class="tfa-description">${t('twoFactorEnterRecovery')}</p>
        <div class="tfa-code-input-group">
          <input type="text" class="tfa-recovery-input" placeholder="xxxx-xxxx" />
        </div>
        <div class="tfa-error" style="display:none;"></div>
        <button type="button" class="tfa-primary-btn" data-action="verify-recovery">${t('twoFactorVerifyRecovery')}</button>
        <button type="button" class="tfa-toggle-link" data-action="toggle-mode">${t('twoFactorUseAuthenticator')}</button>
      </div>
    `;
  }

  function attachEvents() {
    container.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', handleAction);
    });

    const codeInput = container.querySelector('.tfa-code-input');
    if (codeInput) {
      codeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
      });
      codeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const submitBtn = container.querySelector('.tfa-primary-btn');
          if (submitBtn && !submitBtn.disabled) submitBtn.click();
        }
      });
      setTimeout(() => codeInput.focus(), 100);
    }

    const recoveryInput = container.querySelector('.tfa-recovery-input');
    if (recoveryInput) {
      recoveryInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const submitBtn = container.querySelector('.tfa-primary-btn');
          if (submitBtn && !submitBtn.disabled) submitBtn.click();
        }
      });
      setTimeout(() => recoveryInput.focus(), 100);
    }
  }

  function clearError() {
    const errEl = container.querySelector('.tfa-error');
    if (errEl) errEl.style.display = 'none';
  }

  function showError(msg) {
    const errEl = container.querySelector('.tfa-error');
    if (errEl) {
      errEl.textContent = msg;
      errEl.style.display = 'block';
    }
  }

  function setLoading(btn, loading) {
    if (btn) {
      btn.disabled = loading;
      if (loading) btn.dataset.origText = btn.textContent;
      btn.textContent = loading ? '...' : (btn.dataset.origText || btn.textContent);
    }
  }

  async function handleAction(e) {
    const action = e.currentTarget.dataset.action;
    const btn = e.currentTarget;

    if (action === 'back-to-login') {
      onCancel();
      return;
    }

    if (action === 'toggle-mode') {
      useRecovery = !useRecovery;
      render();
      return;
    }

    if (action === 'verify') {
      clearError();
      const code = container.querySelector('.tfa-code-input')?.value;
      if (!code || code.length !== 6) {
        showError(t('twoFactorEnterSixDigit'));
        return;
      }
      setLoading(btn, true);
      try {
        const userData = await twoFactorVerify(tempToken, instance, { code });
        completeLoginAfterTwoFactor(userData, instance);
        onComplete();
      } catch (err) {
        showError(err.message || t('twoFactorInvalidCode'));
        setLoading(btn, false);
      }
      return;
    }

    if (action === 'verify-recovery') {
      clearError();
      const recoveryCode = container.querySelector('.tfa-recovery-input')?.value?.trim();
      if (!recoveryCode) {
        showError(t('twoFactorEnterRecoveryCode'));
        return;
      }
      setLoading(btn, true);
      try {
        const userData = await twoFactorVerify(tempToken, instance, { recoveryCode });
        completeLoginAfterTwoFactor(userData, instance);
        onComplete();
      } catch (err) {
        showError(err.message || t('twoFactorInvalidCode'));
        setLoading(btn, false);
      }
    }
  }

  render();
  return container;
}
